import React from "react";
import { Spinner } from "reactstrap";
import { useTopNetworks } from "../../Hooks/useTopNetworks";
import TopNetworksModal from "./TopNetworksModal";
import { formatPeriodRange } from "../../utils/date";

const VISIBLE_ROWS = 5;

const formatPoints = (points) =>
    new Intl.NumberFormat("es-CO").format(Math.round(points || 0));

/**
 * Un corte puede liquidar en varias monedas. Se muestran todas para no dar a
 * entender que el total está en COP cuando hay USD o EUR de por medio.
 */
const formatMoney = (valueByCurrency) => {
    const entries = Object.entries(valueByCurrency || {}).filter(
        ([, data]) => (data?.value || 0) > 0
    );

    if (entries.length === 0) return "—";

    return entries
        .map(
            ([currency, data]) =>
                `${currency} ${new Intl.NumberFormat("es-CO").format(Math.round(data.value))}`
        )
        .join(" · ");
};

const TopNetworksCard = ({ currency, settlementId, province, city }) => {

    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const { networks, settlement, isLoading, error } = useTopNetworks({
        currency,
        settlementId,
        limit: 10,
        province,
        city
    });

    const visible = networks.slice(0, VISIBLE_ROWS);

    return (

        <div className="ui-card">

            <div className="ui-card__head" style={{ marginBottom: 16 }}>
                <div>
                    <h2 className="ui-card__title">Top redes</h2>
                    <div className="ui-card__sub">
                        {
                            // Sin settlementId el dato sale del caché del
                            // dashboard, que es el mes EN CURSO y todavía no
                            // está liquidado. Llamarlo "corte" haría pensar
                            // que son cifras cerradas.
                            formatPeriodRange(settlement?.period)
                                ? `${settlement?.source === "DASHBOARD_CACHE" ? "Mes en curso" : "Corte"} ${formatPeriodRange(settlement.period)}`
                                : "Mes en curso"
                        }
                    </div>
                </div>

                <button
                    type="button"
                    className="ui-link"
                    disabled={networks.length === 0}
                    onClick={() => setIsModalOpen(true)}
                >
                    Ver todas
                </button>
            </div>

            {isLoading && (
                <div className="text-center py-5">
                    <Spinner size="sm" />
                </div>
            )}

            {!isLoading && error && (
                <div className="ui-note ui-note--danger">
                    No se pudieron cargar las redes.
                </div>
            )}

            {!isLoading && !error && visible.length === 0 && (
                <div className="ui-empty">
                    El último corte no tiene redes con puntos.
                </div>
            )}

            {!isLoading && !error && visible.length > 0 && (
                <div className="d-flex flex-column gap-3">
                    {visible.map((network, index) => (
                        <div
                            key={network.user_id}
                            className="d-flex align-items-center gap-3"
                        >
                            <span className={`ui-rank${index === 0 ? " ui-rank--first" : ""}`}>
                                {index + 1}
                            </span>

                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                    }}
                                >
                                    {network.user?.name || "—"}
                                </div>
                                <div className="ui-mono">
                                    {network.user?.multyhealth_code || "—"}
                                </div>
                            </div>

                            <div className="text-end" style={{ flexShrink: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>
                                    {formatMoney(network.value_by_currency)}
                                </div>
                                <div style={{ color: "#8A91AE", fontSize: 11, whiteSpace: "nowrap" }}>
                                    {network.network_users_count} miembros ·{" "}
                                    {formatPoints(network.total_points)} pts
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <TopNetworksModal
                isOpen={isModalOpen}
                toggle={() => setIsModalOpen(false)}
                networks={networks}
                settlement={settlement}
                formatPoints={formatPoints}
                formatMoney={formatMoney}
            />

        </div>

    );

};

export default TopNetworksCard;
